import React, { useState } from 'react';
import zoomService from '../../services/zoomService';
import { useUser } from '../../context/UserContext';

const ZoomImport = () => {
  const { refreshUser } = useUser();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [pageSize] = useState(20);
  const [error, setError] = useState(null);
  const [importingId, setImportingId] = useState(null);

  const fetchMeetings = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await zoomService.getMeetings({ from, to, pageSize });
      setMeetings(response?.data?.meetings || response?.data?.data?.meetings || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (meetingId) => {
    setError(null);
    setImportingId(meetingId);
    try {
      await zoomService.importMeeting(meetingId);
      // Success
      setMeetings(prev => prev.filter(m => (m.uuid || m.id) !== meetingId));
      refreshUser(); // update counts/entitlements
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to import meeting');
    } finally {
      setImportingId(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Import Zoom Meetings</h4>
        <button
          className="text-sm text-blue-600 hover:underline"
          onClick={() => setOpen(v => !v)}
        >
          {open ? 'Close' : 'Open'}
        </button>
      </div>

      {open && (
        <div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-gray-500">From</label>
              <input className="w-full mt-1 p-2 rounded border" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-gray-500">To</label>
              <input className="w-full mt-1 p-2 rounded border" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              onClick={fetchMeetings}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Fetch Meetings'}
            </button>
            <p className="text-sm text-gray-500">Showing meetings with recordings in selected date range.</p>
          </div>

          {error && (
            <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>
          )}

          <div className="space-y-3">
            {meetings.length === 0 && !loading && (
              <p className="text-sm text-gray-500">No meetings found. Try widening the date range.</p>
            )}

            {meetings.map(meeting => (
              <div key={meeting.uuid || meeting.id} className="p-3 border rounded flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">{meeting.topic || meeting.id}</div>
                  <div className="text-sm text-gray-500">{new Date(meeting.start_time || meeting.date_time || Date.now()).toLocaleString()}</div>
                  <div className="text-sm text-gray-500">{meeting.has_recordings ? 'Has recordings' : 'No recordings'}</div>
                </div>
                <div>
                  <button
                    className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                    onClick={() => handleImport(meeting.uuid || meeting.id)}
                    disabled={!meeting.has_recordings || importingId === (meeting.uuid || meeting.id)}
                  >
                    {importingId === (meeting.uuid || meeting.id) ? 'Importing…' : 'Import'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoomImport;
